/**
 * PortfolioImpactHero — the money headline atop the /neat homepage.
 *
 * Sums the identified potential savings across every open ACT
 * action into one banner: the big dollar figure plus the supporting
 * portfolio stats (how many actions, how many sites, the single biggest
 * opportunity). This is the "how much this finds you" surface — the rest of
 * the page is the breakdown.
 *
 * Chrome: the platform `Card` `top-action` variant — white Paper, elevation-2,
 * 8px radius, the single 4px PRIMARY left stripe (the page's one primary
 * element per the information-budget spec). ACT-zone accent is brand primary,
 * never cerulean (that's the WATCH signature). A faint primary wash plus a
 * washed-out nZero mark give it presence while staying on theme; a pure-CSS
 * rise-in adds life (self-disables under reduced-motion).
 *
 * Copy contract (locked, historical framing): the figure is the OBSERVED
 * overspend over the analysis window (not annualized), that is RECOVERABLE by acting — the money is already
 * being spent, so the figure is measured, never
 * "projected", or "forecast". Matches the ACT cards ("observed").
 *
 * Pure/presentational: renders from `ActionCardSlot[]` only. Renders nothing
 * when there are no open actions — the parent shows the all-clear hero then.
 */
import type React from "react"
import { useTranslation } from "react-i18next"

import { useFormatCurrency } from "@/formatters/use-format-currency"
import { FONT_WEIGHT } from "@/models/typography"

import {
  BoltRounded,
  PlaceRounded,
  TrendingUpRounded,
} from "@mui/icons-material"
import { Box, Divider, Stack, Typography } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"

import { Card, Eyebrow } from "./atoms"
import type { ActionCardSlot } from "./insights-homepage.types"

// ── stat block (plain — no pill chrome) ──────────────────────────────────────

const StatBlock: React.FC<{
  label: string
  value: string
  icon: React.ReactNode
  /** Optional second line (e.g. the site behind a $ figure) — wraps freely. */
  caption?: string
  /** Brand-emphasized value (the standout top-opportunity figure). */
  accent?: boolean
}> = ({ label, value, icon, caption, accent = false }) => (
  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <Box
        sx={{
          display: "inline-flex",
          color: accent ? "primary.main" : "text.secondary",
          "& svg": { fontSize: 14 },
        }}
      >
        {icon}
      </Box>
      <Typography
        color="text.secondary"
        sx={{
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontWeight: "bold",
        }}
        variant="dataLabel"
      >
        {label}
      </Typography>
    </Stack>
    <Typography
      noWrap
      sx={{
        color: accent ? "primary.dark" : "text.primary",
        fontWeight: "bold",
      }}
      variant="body2"
    >
      {value}
    </Typography>
    {caption ? (
      <Typography color="text.secondary" variant="dataLabel">
        {caption}
      </Typography>
    ) : null}
  </Stack>
)

// ── hero ────────────────────────────────────────────────────────────────────

export const PortfolioImpactHero: React.FC<{
  cards: readonly ActionCardSlot[]
  /** Analysis window under consideration (e.g. "the trailing 12 months"). */
  periodLabel?: string
}> = ({ cards, periodLabel }) => {
  const theme = useTheme()
  const { t } = useTranslation("common")
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  if (cards.length === 0) {
    return null
  }

  const total = cards.reduce((sum, c) => sum + c.impact_dollars, 0)
  const actionCount = cards.length
  const siteCount = new Set(cards.map((c) => c.site_label)).size
  const top = cards.reduce((best, c) =>
    c.impact_dollars > best.impact_dollars ? c : best
  )

  return (
    <Card
      aria-label="Portfolio impact"
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        p: { xs: "20px 22px", sm: "26px 30px" },
        backgroundColor: "common.white",
        backgroundImage: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.07
        )} 0%, ${alpha(theme.palette.primary.main, 0.015)} 40%, transparent 72%)`,
        "@keyframes impactRiseIn": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "none" },
        },
        animation: "impactRiseIn .5s ease-out both",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
      variant="top-action"
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2.5, md: 4 },
          alignItems: { md: "center" },
          justifyContent: "space-between",
        }}
      >
        {/* Money block — the dominant element. */}
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Eyebrow sx={{ color: "primary.main" }}>
            {t("neat.identifiedPotentialSavings")}
          </Eyebrow>
          <Typography
            component="p"
            sx={{
              color: "primary.dark",
              fontWeight: FONT_WEIGHT.bold,
              fontSize: { xs: "2.375rem", sm: "3rem" },
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatCurrency(total)}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ maxWidth: 340, lineHeight: 1.4 }}
            variant="body2"
          >
            recoverable by acting on the recommendations below
          </Typography>
          {periodLabel ? (
            <Eyebrow sx={{ mt: 0.5 }}>{periodLabel} analysis window</Eyebrow>
          ) : null}
        </Stack>

        {/* Divider only when the composition stacks (xs). */}
        <Divider
          sx={{
            display: { xs: "block", md: "none" },
            borderColor: alpha(theme.palette.primary.main, 0.12),
          }}
        />

        {/* Stat rail — supporting figures, aligned as one deliberate group. */}
        <Stack
          direction="row"
          divider={<Divider flexItem orientation="vertical" />}
          spacing={{ xs: 2.5, md: 3.5 }}
          sx={{ flex: "0 0 auto", alignItems: "stretch" }}
        >
          <StatBlock
            icon={<BoltRounded />}
            label="Actions"
            value={String(actionCount)}
          />
          <StatBlock
            icon={<PlaceRounded />}
            label="Sites"
            value={String(siteCount)}
          />
          <StatBlock
            accent
            caption={top.site_label}
            icon={<TrendingUpRounded />}
            label="Top opportunity"
            value={formatCurrency(top.impact_dollars)}
          />
        </Stack>
      </Box>
    </Card>
  )
}
